<script lang="ts">
  import { onMount, onDestroy, createEventDispatcher } from "svelte";
  
  export let page;
  const dispatch = createEventDispatcher();
  
  let canvas;
  let width;
  let height;
  let clientWidth;
  let mounted;
  
  // High-resolution rendering variables
  const pixelRatio = window.devicePixelRatio || 1;
  const RENDER_SCALE = Math.max(2, pixelRatio * 1.5);
  let renderTask = null;
  
  function measure() {
    dispatch("measure", {
      scale: canvas.clientWidth / width,
      image: canvas.toDataURL("image/jpeg", 0.95),
    });
  }

  async function render() {
    try {
      // Cancel any existing render task
      if (renderTask) {
        renderTask.cancel();
      }

      const _page = await page;
      const context = canvas.getContext("2d");
      
      if (!context) {
        throw new Error("Could not get 2D context from canvas");
      }

      // Get base viewport for display dimensions (maintains original logic)
      const baseViewport = _page.getViewport({
        scale: 1,
        rotation: _page._pageInfo.rotate,
      });
      
      // Set display dimensions (same as original)
      width = baseViewport.width;
      height = baseViewport.height;

      // Get high-resolution viewport for rendering
      const renderViewport = _page.getViewport({
        scale: RENDER_SCALE,
        rotation: _page._pageInfo.rotate,
      });

      // Set canvas internal resolution to high-res
      canvas.width = renderViewport.width;
      canvas.height = renderViewport.height;

      // Clear and configure context for high quality
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';

      // Render at high resolution
      renderTask = _page.render({
        canvasContext: context,
        viewport: renderViewport,
      });

      await renderTask.promise;
      renderTask = null;

      measure();
      window.addEventListener("resize", measure);

    } catch (error) {
      if (error.name === 'RenderingCancelledException') {
        console.log('Rendering cancelled');
      } else {
        console.error('Error rendering PDF page:', error);
      }
    }
  }

  onMount(() => {
    mounted = true;
    render();
  });
  
  onDestroy(() => {
    mounted = false;
    if (renderTask) {
      renderTask.cancel();
    }
    window.removeEventListener("resize", measure);
  });
</script>

<div class="pdf-page-wrap">
  <canvas
    bind:this={canvas}
    class="max-w-full"
    style="
      width: {width}px;
      image-rendering: -webkit-optimize-contrast;
      image-rendering: crisp-edges;
    "
    width={canvas?.width || width}
    height={canvas?.height || height}
  />
</div>

<style>
  .pdf-page-wrap {
    display: inline-block;
    max-width: 100%;
    line-height: 0;
  }

  canvas {
    /* High-quality rendering */
    image-rendering: -webkit-optimize-contrast;
    image-rendering: -moz-crisp-edges;
    image-rendering: crisp-edges;
    
    /* Hardware acceleration for smooth rendering */
    transform: translateZ(0);
    backface-visibility: hidden;
    
    /* Prevent blurriness */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  /* Mobile only — scale page to viewport without changing desktop */
  @media (max-width: 767px) {
    canvas {
      width: 100% !important;
      max-width: 100% !important;
      height: auto !important;
    }
  }
</style>
